import * as React from "react";
import CssBaseline from "@mui/joy/CssBaseline";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Checkbox from "@mui/joy/Checkbox";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import IconButton from "@mui/joy/IconButton";
import Input from "@mui/joy/Input";
import Typography from "@mui/joy/Typography";
import Stack from "@mui/joy/Stack";
import axiosInstance from '../axiosInstance';
import useSignIn from "react-auth-kit/hooks/useSignIn";
import { useNavigate } from "react-router-dom";
import Tooltip from "@mui/joy/Tooltip";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Visibility from "@mui/icons-material/Visibility";
import InfoIcon from "@mui/icons-material/Info";
import Alert from "@mui/joy/Alert";
import dacapiLogo from "./../../Components/Assets/logo_new.png";
import { useUser } from "../../Components/UserContext";
import axios from "axios";
import posthog from 'posthog-js'
import { COLORS } from "../../helpers/theme";
import { useSpring, animated, to as springTo } from "@react-spring/web";


interface UserContextType { fetchUser: () => Promise<void>; }

interface FormElements extends HTMLFormControlsCollection {
  email: HTMLInputElement;
  password: HTMLInputElement;
  persistent: HTMLInputElement;
}
interface SignInFormElement extends HTMLFormElement { readonly elements: FormElements; }

export default function JoySignInSideTemplate() {
  const signIn = useSignIn();
  const navigate = useNavigate();
  const { fetchUser } = useUser() as UserContextType;
  const [error, setError] = React.useState<string | null>(null);
  const [showPassword, setShowPassword] = React.useState(false);
  const colors = COLORS.dark;

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (event: React.FormEvent<SignInFormElement>) => {
    event.preventDefault();
    const formElements = event.currentTarget.elements;
    const username = formElements.email.value.trim();
    const password = formElements.password.value.trim();
    const rememberMe = formElements.persistent.checked;
  
    try {
      const response = await axiosInstance.post(
        "/auth/token",
        `grant_type=password&username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}&scope=&client_id=&client_secret=`,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );
  
      if (response.status === 200) {
        posthog.capture('user_logged_in', {
          username: username,
          timestamp: new Date().toISOString(),
          distinct_id: username
        });
      }
  
      const { access_token } = response.data;
      const payload = JSON.parse(atob(access_token.split(".")[1]));
      const userRole = payload.role;
  
      signIn({
        auth: {
          token: access_token,
        },
        userState: { name: username, role: userRole },
      });
  
      localStorage.setItem("token", access_token);
      localStorage.setItem("role", userRole);
      if (rememberMe) {
        localStorage.setItem("username", username);
        localStorage.setItem("password", password);
      }
  
      await fetchUser();
  
      if (userRole === "instalator") {
        navigate("/installation", { replace: true });
      } else {
        const redirectAfterLogin = localStorage.getItem("redirectAfterLogin");
        if (redirectAfterLogin) {
          localStorage.removeItem("redirectAfterLogin");
          navigate(redirectAfterLogin, { replace: true });
        } else {
          navigate("/", { replace: true });
        }
      }
    } catch (error: any) {
      if (axios.isAxiosError(error) && error.response) {
        setError(error.response.data.detail);
      } else {
        setError("An unexpected error occurred. Please try again.");
        console.log("Error: ", error);
      }
    }
  };
  
  // Entrance animation for the logo (opacity + blur + pop + slight rotate + glow)
  const logoSpring = useSpring({
    from: { opacity: 0, y: -24, s: 0.8, r: -8, blur: 14, glow: 0 },
    to: async (next) => {
      await next({ opacity: 1, y: 0, s: 1.06, r: 0, blur: 0, glow: 42 });
      await next({ s: 1.0, glow: 16 });
    },
    config: { tension: 240, friction: 18 },
    delay: 180,
  });
  return (
    <>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
            background: `
                       radial-gradient(1200px 600px at 65% 35%, ${colors.primary}99, transparent 60%),
                       radial-gradient(900px 500px at 20% 80%, ${colors.borders.accent}47, transparent 62%),
                       radial-gradient(1000px 520px at 15% 20%, ${colors.primaryLight}59, transparent 62%),
                       radial-gradient(800px 420px at 85% 80%, ${colors.text.secondary}1F, transparent 64%),
                       linear-gradient(180deg, ${colors.background.secondary}, ${colors.background.default}),
                       ${colors.background.default}`,
          p: 2,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: 500,
            maxWidth: '94vw',
            p: 4,
            borderRadius: 2,
            overflow: 'visible',
            backgroundColor: colors.background.paper,
            backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0))',
            boxShadow: `inset 0 1px 0 rgba(255,255,255,0.04), 0 20px 60px rgba(0,0,0,0.65), 0 0 24px ${colors.primary}26`,
            '&:before': {
              content: '""',
              position: 'absolute',
              inset: '-80px',
            
              zIndex: -1,
              filter: 'blur(20px)',
              pointerEvents: 'none',
            },
          }}
        >
          <Stack gap={2} alignItems="center" sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
              <animated.img
                src={dacapiLogo}
                alt="Logo"
                style={{
                  height: 120,
                  opacity: logoSpring.opacity,
                  transform: springTo([logoSpring.y, logoSpring.s, logoSpring.r], (y, s, r) => `translateY(${y}px) scale(${s}) rotateZ(${r}deg)`),
                  filter: logoSpring.blur.to((b) => `blur(${b}px)`),
                  
                  willChange: 'transform, opacity, filter, box-shadow',
                }}
              />
            </Box>
          </Stack>

          <form onSubmit={handleSubmit}>
            {error && (
              <Alert variant="outlined" color="danger" role="alert" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <FormControl required>
              <FormLabel sx={{ color: colors.text.secondary }}>Login</FormLabel>
              <Input
                type="text"
                name="email"
                autoFocus
                color="neutral"
                sx={{
                  '--Input-radius': '6px',
                  '--Input-minHeight': '44px',
                  '--Input-focusedHighlight': 'transparent',
                  '--Input-focusedThickness': '0px',
                  bgcolor: colors.background.input,
                  color: colors.text.primary,
                  border: `1px solid ${colors.borders.primary}`,
                  '&:hover': { borderColor: colors.borders.accent },
                  '&:focus-within': {
                    borderColor: colors.borders.primary,
                    boxShadow: 'none'
                  },
                  '& input:focus': {
                    outline: 'none',
                  },
                  '&[data-focused=true]': {
                    borderColor: colors.borders.primary,
                  },
                }}
                defaultValue={localStorage.getItem('username') || ''}
              />
            </FormControl>

            <FormControl required sx={{ mt: 1.5 }}>
              <FormLabel sx={{ color: colors.text.secondary }}>Password</FormLabel>
              <Input
                type={showPassword ? 'text' : 'password'}
                name="password"
                color="neutral"
                sx={{
                  '--Input-radius': '6px',
                  '--Input-minHeight': '44px',
                  '--Input-focusedHighlight': 'transparent',
                  '--Input-focusedThickness': '0px',
                  bgcolor: colors.background.input,
                  color: colors.text.primary,
                  border: `1px solid ${colors.borders.primary}`,
                  '&:hover': { borderColor: colors.borders.accent },
                  '&:focus-within': {
                    borderColor: colors.borders.primary,
                    boxShadow: 'none'
                  },
                  '& input:focus': {
                    outline: 'none',
                  },
                  '&[data-focused=true]': {
                    borderColor: colors.borders.primary,
                  },
                }}
                defaultValue={localStorage.getItem('password') || ''}
                endDecorator={
                  <IconButton onClick={togglePasswordVisibility} component="span" variant="plain" color="neutral" sx={{ color: colors.text.secondary, '&:hover': { color: colors.text.secondary, backgroundColor: colors.background.secondary } }}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                }
              />
            </FormControl>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 1.5,
                mb: 2,
              }}
            >
              <Checkbox
                size="sm"
                name="persistent"
                variant="outlined"
                disableIcon={false}
                label={
                  <Typography level="body-sm" sx={{ color: colors.text.secondary }}>
                    Pamiętaj mnie
                  </Typography>
                }
                sx={{
                  '--Checkbox-size': '18px',
                  '--Checkbox-radius': '4px',
                  '--Icon-fontSize': '14px',
                  color: colors.text.primary,
                  '& .MuiCheckbox-checkbox, & .joy-Checkbox-checkbox': {
                    backgroundColor: colors.background.input,
                    border: `1px solid ${colors.borders.primary}`,
                  },
                  '&:hover .MuiCheckbox-checkbox, &:hover .joy-Checkbox-checkbox': {
                    borderColor: colors.borders.accent,
                  },
                  '&.Mui-checked .MuiCheckbox-checkbox, &[data-checked=true] .joy-Checkbox-checkbox': {
                    backgroundColor: colors.primary,
                    borderColor: colors.primary,
                  },
                }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: colors.text.secondary }}>
                <Typography level="body-sm">Zapomniałeś hasła?</Typography>
                <Tooltip title="Jeśli zapomniałeś hasła, skontaktuj się z administratorem (Maciem)">
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Box>
            </Box>

            <Button
              type="submit"
              fullWidth
              sx={{
                '--Button-radius': '6px',
                bgcolor: colors.primaryButton,
                color: '#000',
                fontWeight: 600,
                p: 1.2,
                '&:hover': {
                  bgcolor: colors.primaryHover,
                },
              }}
            >
              Zaloguj
            </Button>
          </form>

          <Typography level="body-xs" textAlign="center" sx={{ mt: 3, color: colors.text.secondary }}>
            © DACSYSTEM {new Date().getFullYear()}
          </Typography>
        </Box>
      </Box>
    </>
  );
}
